import type { Market, MarketOutcome, OrderBook } from "./types";
import { toPmapiMarketId } from "./lib/http";
import type { PolymarketClobBook, PolymarketGammaMarket } from "./wire";

const VENUE = "polymarket" as const;

export function mapGammaMarket(raw: PolymarketGammaMarket): Market | null {
  if (!raw.question) return null;

  const labels = parseJsonArray<string>(raw.outcomes);
  const prices = parseJsonArray<string>(raw.outcomePrices);
  const tokens = parseJsonArray<string>(raw.clobTokenIds);

  const outcomes: MarketOutcome[] = labels.map((label, i) => ({
    tokenId: tokens[i] ?? `${raw.id}:${i}`,
    label,
    lastPrice: prices[i],
    midPrice: prices[i],
  }));
  if (outcomes.length === 0) {
    // Some markets are still rendered without explicit outcome arrays — fall
    // back to a binary YES/NO so the question is at least browseable.
    outcomes.push(
      { tokenId: `${raw.id}:YES`, label: "YES" },
      { tokenId: `${raw.id}:NO`, label: "NO" }
    );
  }

  const venueId = raw.conditionId ?? raw.id;
  return {
    id: toPmapiMarketId(VENUE, venueId),
    venue: VENUE,
    venueMarketId: venueId,
    slug: raw.slug,
    question: raw.question,
    description: raw.description,
    category: raw.category,
    kind: outcomes.length > 2 ? "multi" : "binary",
    outcomes,
    endTime: raw.endDate,
    active: raw.active === true && raw.closed !== true && raw.archived !== true,
    liquidityUsd: raw.liquidity,
    volume24hUsd: raw.volume24hr,
    volumeTotalUsd: raw.volume,
    tags: raw.tags?.map((t) => t.slug),
    imageUrl: raw.image ?? raw.icon,
    updatedAt: new Date().toISOString(),
  };
}

export function mapClobBook(tokenId: string, raw: PolymarketClobBook): OrderBook {
  const marketAsset = raw.market ?? raw.asset_id ?? tokenId;
  return {
    marketId: toPmapiMarketId(VENUE, marketAsset),
    tokenId,
    venue: VENUE,
    bids: (raw.bids ?? []).map((l) => ({ price: l.price, size: l.size })),
    asks: (raw.asks ?? []).map((l) => ({ price: l.price, size: l.size })),
    timestamp: raw.timestamp ?? new Date().toISOString(),
  };
}

function parseJsonArray<T>(raw: string | undefined): T[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
